/* eslint-disable max-lines-per-function */
/* eslint-disable @typescript-eslint/no-empty-function */
/* istanbul ignore file */
import { computed, reactive } from '@nuxtjs/composition-api';
import useScrollLock from '@shared/composables/useScrollLock';
import {
  lockPageScrollOnMobileOnly,
  unlockPageScrollOnMobileOnly,
} from '@shared/composables/useUiHelpers';
import stringParseClone from '@shared/helpers/stringParseClone';
import { UserAddress } from '@unified-commerce/gpc-vue-storefront-shopify';
const { toggleScrollLock } = useScrollLock();

const state = reactive({
  isCartSidebarOpen: false,
  isRecentlyAddedSidebarOpen: false,
  isWishlistSidebarOpen: false,
  isGarageModalOpen: false,
  isAccountModalOpen: false,
  isRemoveAddressModalOpen: false,
  addressToRemove: null as UserAddress | null,
  isSignup: false,
  isLogoutModalOpen: false,
  isCategoryGridView: true,
  isFilterSidebarOpen: false,
  isNavigationSidebarOpen: false,
  isStoresSidebarOpen: false,
  recentlyAddedProduct: null as {
    variantId: string;
    name: string;
    brand: string;
    price: number;
    compareToPrice?: number | null;
    specialPrice?: number | null;
    partNumber: string;
    sku: string;
    img?: string;
  } | null,
});

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
const useUiState = () => {
  const isCartSidebarOpen = computed(() => state.isCartSidebarOpen);
  const toggleCartSidebar = () => {
    state.isCartSidebarOpen = !state.isCartSidebarOpen;
    if (state.isCartSidebarOpen) {
      lockPageScrollOnMobileOnly();
    } else {
      unlockPageScrollOnMobileOnly();
    }
  };

  const isWishlistSidebarOpen = computed(() => state.isWishlistSidebarOpen);
  const toggleWishlistSidebar = () => {
    state.isWishlistSidebarOpen = !state.isWishlistSidebarOpen;
  };

  const isNavigationSidebarOpen = computed(() => state.isNavigationSidebarOpen);
  const toggleNavigationSidebar = () => {
    state.isNavigationSidebarOpen = !state.isNavigationSidebarOpen;
  };

  const isGarageModalOpen = computed(() => state.isGarageModalOpen);
  const setGarageModalOpen = (isOpen: boolean) => {
    state.isGarageModalOpen = isOpen;
  };

  const isStoresSidebarOpen = computed(() => state.isStoresSidebarOpen);
  const toggleStoresSidebar = (open?: boolean) => {
    state.isStoresSidebarOpen = typeof open === 'boolean' ? open : !state.isStoresSidebarOpen;
  };
  const isAccountModalOpen = computed(() => state.isAccountModalOpen);

  const showAccountModal = () => {
    toggleScrollLock();
    state.isAccountModalOpen = true;
  };
  const toggleAccountModal = () => {
    toggleScrollLock();
    state.isAccountModalOpen = !state.isAccountModalOpen;
  };

  const isRemoveAddressModalOpen = computed(() => state.isRemoveAddressModalOpen);
  const addressToRemove = computed(() => stringParseClone(state.addressToRemove));
  const showRemoveAddressModal = (address?: UserAddress) => {
    state.isRemoveAddressModalOpen = true;
    if (address) {
      state.addressToRemove = address;
    }
  };
  const toggleRemoveAddressModal = () => {
    state.isRemoveAddressModalOpen = !state.isRemoveAddressModalOpen;
    if (state.isRemoveAddressModalOpen) {
      lockPageScrollOnMobileOnly();
    } else {
      state.addressToRemove = null;
      unlockPageScrollOnMobileOnly();
    }
  };

  const isSignup = computed(() => state.isSignup);

  const setIsSignup = (boolean: boolean) => {
    state.isSignup = boolean;
  };

  const isLogoutModalOpen = computed(() => state.isLogoutModalOpen);
  const showLogoutModal = () => {
    state.isLogoutModalOpen = true;
  };
  const toggleLogoutModal = () => {
    state.isLogoutModalOpen = !state.isLogoutModalOpen;
  };
  const closeLogoutModal = () => {
    state.isLogoutModalOpen = false;
  };

  const isCategoryGridView = computed(() => state.isCategoryGridView);
  const toggleCategoryGridView = () => {
    state.isCategoryGridView = !state.isCategoryGridView;
  };

  const isFilterSidebarOpen = computed(() => state.isFilterSidebarOpen);
  const toggleFilterSidebar = () => {
    state.isFilterSidebarOpen = !state.isFilterSidebarOpen;
  };

  const recentlyAddedProduct = computed(() => state.recentlyAddedProduct);
  const isRecentlyAddedSidebarOpen = computed(() => !!state.recentlyAddedProduct);
  const setRecentlyAddedProduct = (
    product: {
      variantId: string;
      name: string;
      brand: string;
      price: number;
      compareToPrice?: number | null;
      specialPrice?: number | null;
      partNumber: string;
      sku: string;
      img?: string;
    } | null,
  ) => {
    state.recentlyAddedProduct = product;
  };

  return {
    isCartSidebarOpen,
    isWishlistSidebarOpen,
    isAccountModalOpen,
    isRemoveAddressModalOpen,
    isGarageModalOpen,
    isSignup,
    setIsSignup,
    isLogoutModalOpen,
    isCategoryGridView,
    isFilterSidebarOpen,
    isNavigationSidebarOpen,
    isStoresSidebarOpen,
    addressToRemove,
    toggleStoresSidebar,
    toggleCartSidebar,
    toggleWishlistSidebar,
    toggleAccountModal,
    showAccountModal,
    toggleRemoveAddressModal,
    showRemoveAddressModal,
    toggleLogoutModal,
    closeLogoutModal,
    showLogoutModal,
    toggleCategoryGridView,
    toggleFilterSidebar,
    toggleNavigationSidebar,
    setGarageModalOpen,
    recentlyAddedProduct,
    setRecentlyAddedProduct,
    isRecentlyAddedSidebarOpen,
  };
};

export default useUiState;
